"use strict";

/** Class representing a PUFF-J file */
module.exports = class PuffJ {
  /**
   *
   * @param {object} options - JS object holding a single key, "text", a PUFF-J stringified
   */
  constructor(options) {
    let defaults = {
      exportFormat: 'JSON',
      text: JSON.stringify({
        note: 'Generated by Katal Localization',
        direction: 'ltr',
        resources: {}
      })
    };
    this.options = Object.assign({}, defaults, options);
    this.data = JSON.parse(this.options.text);
    this.validate();
  }
  /**
   * Validates the PUFF-J for structural correctness
   * At the moment, this only throws an error if the PUFF-J contains a 'pluralItems' translation
   * as described on the wiki:
   * https://w.amazon.com/bin/view/INTech/Panther/Architecture/PantherFileFormat/JSON/#HPluraltype
   */


  validate() {
    const values = Object.values(this.data.resources);
    const plurals = values.filter(resource => {
      return resource.value && resource.value.pluralItems;
    });

    if (plurals && plurals.length) {
      throw new Error('Unable to process this PUFF-J. Plurals are not supported yet.\n\n' + 'Please use ICU format with `i18next-icu` package. Example resource in ICU format:\n' + '"apples_in_basket": "{appleCount, plural, =0 {You have no apples in a basket} one {You have {appleCount} apple in a basket} other {You have {appleCount} apples in a basket}}"');
    }
  }
  /**
   * Retrieve a string from a PUFF-J
   * @param {string} stringId - The string id to retrieve
   * @param {object} metadata - Metadata used to retrieve a string. Can be null/undefined for simple retrieval, or contain "selectItemsValue" for lookup in a selectItems switch-case
   */


  get(stringId, metadata) {
    // No string exists with this id
    if (!this.data.resources[stringId]) {
      return;
    }

    const stringIdResource = this.data.resources[stringId]; // No metadata provided means a simple 'get' with no switching/resolution

    const resourceTypes = ['string', 'number'];

    if (resourceTypes.includes(typeof stringIdResource)) {
      return stringIdResource;
    } // A PUFF-J resource may contain additional attributes like `note` or `dir`


    const value = stringIdResource.value;

    if (resourceTypes.includes(typeof value)) {
      return value;
    }

    const selectItems = value.selectItems;
    const parameterValue = metadata.selectItemsValue; // If a specific selectItems parameter was passed, use it. Otherwise, return the map of parameter-to-text

    let returnValue;

    if (parameterValue) {
      returnValue = selectItems[parameterValue];
    } else {
      returnValue = selectItems;
    }

    return returnValue;
  }
  /**
   * Get all string ids
   */


  getStringIds() {
    return Object.keys(this.data.resources);
  }
  /**
   * Insert a string into the PUFF-J
   * @param {string} stringId - String id to insert into
   * @param {string} text - The text to insert
   * @param {object} metadata - Metadata for insertion. Can be null/undefined, or contain "selectItemsValue" and "selectItemsParameter" for switch-cases
   */


  insert(stringId, text, metadata) {
    // No metadata provided means a simple 'get' with no switching/resolution
    if (!metadata || Object.keys(metadata).length === 0) {
      var existingValue = this.get(stringId, metadata);

      if (existingValue) {
        throw new Error('Cannot insert translation when one already exists');
      }

      this.data.resources[stringId] = text;
      return;
    } // Try looking up string id for complex case


    let resource = this.data.resources[stringId]; // Create it as a selectItems in its entirety if it doesn't already exist
    // and return

    if (!resource) {
      this.data.resources[stringId] = {
        value: {
          param: metadata.selectItemsParameter,
          selectItems: {
            [metadata.selectItemsValue]: text
          }
        }
      };
      return;
    } // Check if the selectItems parameter matches
    // This prevents string values switched on multiple parameters at the same time


    if (resource.value.param !== metadata.selectItemsParameter) {
      throw new Error(`Cannot insert the same string id twice because the previously-inserted string was configured to switch on "${resource.value.param}" but the second string is switched on "${metadata.selectItemsParameter}"`);
    }

    const existingText = this.get(stringId, metadata);

    if (existingText && text !== existingText) {
      throw new Error(`Cannot insert the same string id (${stringId}) to the same selectItem value (${JSON.stringify(metadata)}). ` + `This string id appears more than once with multiple values ("${text}" vs "${existingText}")`);
    }

    resource.value.selectItems[metadata.selectItemsValue] = text;
  }
  /**
   * Remove a string from a PUFF-J
   * @param {string} stringId - Id of the string to remove
   * @param {object} metadata - Metadata for deletion. Can be null/undefined, or contain "selectItemsValue" and "selectItemsParameter" for switch-cases
   */


  remove(stringId, metadata) {
    // No metadata provided means a simple removal with no switching/resolution
    if (!metadata || Object.keys(metadata).length === 0) {
      delete this.data.resources[stringId];
      return;
    }

    delete this.data.resources[stringId].value.selectItems[metadata.selectItemsValue];

    if (Object.keys(this.data.resources[stringId].value.selectItems).length === 0) {
      delete this.data.resources[stringId];
    }
  }
  /**
   * Copies all data out of a target PUFF-J into this PUFF-J
   * Throws exception if a string id would be overwritten to prevent errors
   * @param {PuffJ} targetPuffJ
   */


  copyFrom(targetPuffJ) {
    // Pull list of ids out of target PUFF-J file
    let idsList = Object.keys(targetPuffJ.data.resources);
    idsList.forEach(id => {
      if (this.data.resources[id]) {
        throw new Error(`Cannot copy string id '${id}' out of PUFF-J as it already exists`);
      }

      this.data.resources[id] = targetPuffJ.data.resources[id];
    });
  }
  /**
   * Explicitly mark this PUFF-J as containing right-to-left text.
   * This does not affect Katal Localization but could possibly affect Panther,
   * translation request workflows, and other downstream tools.
   */


  setRTL() {
    this.data.direction = 'rtl';
  }
  /**
   * Serializes this PUFF-J into JS object form
   */


  toObject() {
    return this.data;
  }
  /**
   * Serializes this PUFF-J into a JavaScript string
   */


  toString() {
    return JSON.stringify(this.data, null, 4);
  }

};